/**
 * Générateur — Automatisme : convertir en mètre (et inversement)
 * Attendu ID : 1022 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait convertir en
 * mètre une longueur donnée dans une autre unité, multiple ou sous-multiple
 * du mètre. Inversement, l'élève sait convertir dans une unité donnée une
 * longueur exprimée en mètre. »
 * Labels (cartes exerciseur) :
 *   - Convertir en mètre (et inversement)
 *
 * ── Plan de cartes (2026-08-21, validé prof) ───────────────────────────────
 * PÉRIMÈTRE : le mètre est TOUJOURS l'une des deux unités (garde qui throw) ;
 * l'autre parcourt km..mm ; les deux sens à parts égales. Valeurs : 1 à 2
 * chiffres significatifs (3 chiffres ~1/5, comme « 1 250 m » de la flash),
 * ≤ 3 décimales DES DEUX CÔTÉS (le CM2 s'arrête aux millièmes), ≤ 4 chiffres
 * avant la virgule. PROCÉDURE prescrite : la relation VERBALISÉE avec son
 * « car » (« 3,5 m = 350 cm, car 1 m = 100 cm ») — jamais de tableau.
 * COMMUTATIVITÉ (vigilance prof) : les deux trous du format « car » portent
 * des valeurs distinctes ancrées chacune à son égalité (l'échange rend les
 * deux fausses) ; le cas dégénéré donnée = 1 (résultat = relation) est
 * BANNI par garde.
 * ARITHMÉTIQUE : tout en MICRO-MÈTRES ENTIERS (1 µm = 10⁻⁶ m) — jamais de
 * flottant ; « ≤ 3 décimales en m » ⇔ divisible par 1 000 µm, etc.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
} from '../../../utils';

const ATTENDU_ID = 1022;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Convertir en mètre (et inversement)';

// ─── Unités et arithmétique en micro-mètres entiers ───────────────────────

const UNITES = [
    { sym: 'km', exp: 3 }, { sym: 'hm', exp: 2 }, { sym: 'dam', exp: 1 },
    { sym: 'dm', exp: -1 }, { sym: 'cm', exp: -2 }, { sym: 'mm', exp: -3 },
];
const EXP = { km: 3, hm: 2, dam: 1, m: 0, dm: -1, cm: -2, mm: -3 };

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1022 : ${message}`);
};

/** Valeur en micro-mètres (entier) → écriture française dans l'unité u.
 *  Retourne null si la valeur demanderait plus de 3 décimales dans u. */
const microVersFr = (micro, unite) => {
    garde(Number.isInteger(micro) && micro > 0, 'micro non entier');
    const shift = 6 + EXP[unite];               // µm → unité : 10^shift
    const diviseur = 10 ** (shift - 3);         // 3 décimales max
    if (shift < 3 || micro % diviseur !== 0) return null;
    let s = String(micro / diviseur);           // valeur × 1000, entière
    s = s.padStart(4, '0');
    let ent = s.slice(0, -3).replace(/^0+(?=\d)/, '');
    let dec = s.slice(-3).replace(/0+$/, '');
    // espaces des milliers sur la partie entière (convention d'affichage)
    ent = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return dec === '' ? ent : `${ent},${dec}`;
};

/** Écriture française → micro-mètres (entier), null si invalide. */
const frVersMicro = (txt, unite) => {
    const m = String(txt).replace(/[\s  ]/g, '').match(/^(\d+)(?:,(\d{1,3}))?$/);
    if (!m) return null;
    const dec = m[2] || '';
    const entier = parseInt(m[1] + dec, 10);
    const shift = 6 + EXP[unite] - dec.length;
    if (shift < 0) return null;
    return entier * 10 ** shift;
};

/**
 * Tire une conversion : { u, sens, donnee, resultat, relP } où relP est la
 * valeur de la relation-pivot (10, 100 ou 1 000). Toutes les écritures ont
 * ≤ 3 décimales et ≤ 4 chiffres avant la virgule ; donnée ≠ 1 (garde
 * anti-commutativité) ; le mètre est toujours l'une des deux unités.
 */
const tirerConversion = () => {
    for (let essai = 0; essai < 200; essai++) {
        const u = randomChoice(UNITES);
        const sens = randomChoice(['vers_m', 'depuis_m']);
        // 1-2 chiffres significatifs, 3 à ~20 %
        const sig = Math.random() < 0.2 ? randInt(101, 999) : randInt(2, 99);
        const p = randInt(-3, 3);               // position de la virgule
        const uniteDonnee = sens === 'vers_m' ? u.sym : 'm';
        const uniteResultat = sens === 'vers_m' ? 'm' : u.sym;
        // micro-mètres de la donnée : sig × 10^p dans son unité
        const shift = 6 + EXP[uniteDonnee] + p;
        if (shift < 0 || shift > 9) continue;
        const micro = sig * 10 ** shift;
        const donnee = microVersFr(micro, uniteDonnee);
        const resultat = microVersFr(micro, uniteResultat);
        if (donnee === null || resultat === null) continue;
        // ≤ 4 chiffres avant la virgule, des deux côtés
        if (donnee.split(',')[0].replace(/\s/g, '').length > 4) continue;
        if (resultat.split(',')[0].replace(/\s/g, '').length > 4) continue;
        if (donnee === '1') continue;           // cas dégénéré banni
        // relation-pivot : 1 u en m (multiples) ou 1 m en u (sous-multiples)
        const relP = u.exp > 0
            ? microVersFr(10 ** (6 + u.exp), 'm')
            : microVersFr(10 ** 6, u.sym);
        garde(relP !== null && ['10', '100', '1 000'].includes(relP), 'relation-pivot hors table');
        if (resultat === relP) continue;        // trous jumeaux bannis
        garde(uniteDonnee === 'm' || uniteResultat === 'm', 'le mètre doit être dans la paire');
        return { u: u.sym, exp: u.exp, sens, donnee, resultat, relP };
    }
    return { u: 'cm', exp: -2, sens: 'vers_m', donnee: '250', resultat: '2,5', relP: '100' };
};

/** La relation-pivot en toutes lettres : « 1 km = 1 000 m » / « 1 m = 100 cm ». */
const phraseRelation = (c) => (c.exp > 0 ? `1 ${c.u} = ${c.relP} m` : `1 m = ${c.relP} ${c.u}`);

// ─── Format 1 : la conversion justifiée (saisie, le « car » du programme) ─

const genConversionTrouSaisie = () => {
    const c = tirerConversion();
    const uniteResultat = c.sens === 'vers_m' ? 'm' : c.u;
    const relation = c.exp > 0
        ? `1 ${c.u} = {{${c.relP}}} m`
        : `1 m = {{${c.relP}}} ${c.u}`;
    const uniteDonnee = c.sens === 'vers_m' ? c.u : 'm';
    const phrase = `${c.donnee} ${uniteDonnee} = {{${c.resultat}}} ${uniteResultat}, car ${relation}`;
    return makeTexteTrou(
        phrase,
        '<p><b>Convertis, et complète la relation qui le justifie.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : QCM à erreurs nommées ─────────────────────────────────────

/** Décale la virgule d'une écriture (via micro-mètres, jamais de flottant). */
const decale = (c, rangs) => {
    const uniteDonnee = c.sens === 'vers_m' ? c.u : 'm';
    const micro = frVersMicro(c.donnee, uniteDonnee);
    if (micro === null) return null;
    const facteur = 10 ** Math.abs(rangs);
    if (rangs < 0 && micro % facteur !== 0) return null;
    const microDecale = rangs >= 0 ? micro * facteur : micro / facteur;
    if (!Number.isInteger(microDecale) || microDecale < 1) return null;
    // on formate dans l'unité de la DONNÉE : seul le nombre a glissé
    const brut = microVersFr(microDecale, uniteDonnee);
    if (brut === null) return null;
    const nbChiffres = brut.replace(/[\s,]/g, '').length;
    return nbChiffres <= 7 ? brut : null;
};

const genQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const c = tirerConversion();
        const k = Math.abs(c.exp);
        const bonRang = c.sens === 'vers_m' ? c.exp : -c.exp;
        const candidats = [
            { valeur: decale(c, -bonRang), code: 'sens_inverse' },
            { valeur: decale(c, bonRang + Math.sign(bonRang)), code: 'rang_de_trop' },
            { valeur: decale(c, bonRang - Math.sign(bonRang)), code: 'rang_de_moins' },
        ].filter((d) => d.valeur !== null);
        const vus = new Set([c.resultat]);
        const dis = candidats.filter((d) => {
            if (vus.has(d.valeur)) return false;
            vus.add(d.valeur);
            return true;
        }).slice(0, 3);
        if (dis.length < 3) continue;
        const uniteDonnee = c.sens === 'vers_m' ? c.u : 'm';
        const uniteResultat = c.sens === 'vers_m' ? 'm' : c.u;
        const payload = makeQcm(
            [c.resultat, ...dis.map((d) => d.valeur)],
            c.resultat,
            `<p><b>Complète : ${c.donnee} ${uniteDonnee} = ... ${uniteResultat}</b></p>`,
            opts(L1),
            false,
        );
        payload._meta.params = { ...c, amplitude: k, distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(['2,5', '25 000', '25', '0,25'], '2,5',
        '<p><b>Complète : 250 cm = ... m</b></p>', opts(L1), false);
    payload._meta.params = { u: 'cm', sens: 'vers_m', distracteurs: [] };
    return payload;
};

// ─── Format 3 : tableau Vrai/Faux ─────────────────────────────────────────

const genVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const lignes = [];
        const verites = [];
        const vus = new Set();
        for (let t = 0; t < 40 && lignes.length < 4; t++) {
            const c = tirerConversion();
            const cle = `${c.donnee}|${c.u}|${c.sens}`;
            if (vus.has(cle)) continue;
            const bonRang = c.sens === 'vers_m' ? c.exp : -c.exp;
            let affiche = c.resultat;
            if (Math.random() >= 0.5) {
                // faux NOMMÉS : mêmes familles que le QCM
                const fausses = [
                    decale(c, -bonRang),
                    decale(c, bonRang + Math.sign(bonRang)),
                    decale(c, bonRang - Math.sign(bonRang)),
                ].filter((v) => v !== null && v !== c.resultat);
                if (fausses.length === 0) continue;
                affiche = randomChoice(fausses);
            }
            const uniteDonnee = c.sens === 'vers_m' ? c.u : 'm';
            const uniteResultat = c.sens === 'vers_m' ? 'm' : c.u;
            // Vérité RE-CALCULÉE en micro-mètres (jamais l'intention) :
            const estVraie = frVersMicro(affiche, uniteResultat) === frVersMicro(c.donnee, uniteDonnee);
            vus.add(cle);
            lignes.push(`${c.donnee} ${uniteDonnee} = ${affiche} ${uniteResultat}`);
            verites.push(estVraie);
        }
        const nbVraies = verites.filter(Boolean).length;
        if (lignes.length !== 4 || nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        ['250 cm = 2,5 m', '0,8 m = 8 cm', '3,5 km = 3 500 m', '45 mm = 0,45 m'],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 4 : relier (tout vers le mètre) ───────────────────────────────

const genRelier = () => {
    // MÊME nombre dans toutes les sources (décision prof 2026-08-21 :
    // « 19 dam · 19 cm · 19 mm ») : les cibles portent les mêmes chiffres,
    // seule la virgule bouge — impossible d'apparier par ressemblance.
    for (let essai = 0; essai < 60; essai++) {
        const M = randInt(2, 99);
        const nbVoulu = randomChoice([3, 4]);
        const tirage = [];
        for (const u of shuffle([...UNITES])) {
            if (tirage.length >= nbVoulu) break;
            const micro = frVersMicro(String(M), u.sym);
            if (micro === null) continue;
            const resultat = microVersFr(micro, 'm');
            if (resultat === null) continue;
            if (resultat.split(',')[0].replace(/\s/g, '').length > 4) continue;
            tirage.push({ u: u.sym, exp: u.exp, sens: 'vers_m', donnee: String(M), resultat });
        }
        if (tirage.length < 3) continue;
        const valeursM = new Set(tirage.map((c) => c.resultat));
        if (valeursM.size !== tirage.length) continue;
        const pairs = tirage.map((c) => ({ source: `${c.donnee} ${c.u}`, target: `${c.resultat} m` }));
        // leurres : un glissement fautif du même nombre, jamais égal à une
        // valeur juste du tirage
        const leurres = [];
        for (const c of shuffle([...tirage])) {
            if (leurres.length >= 1) break;
            const faux = decale(c, c.exp + Math.sign(c.exp));
            if (faux === null || valeursM.has(faux)) continue;
            leurres.push({ target: `${faux} m`, code: 'rang_de_trop' });
        }
        const payload = makeRelier(
            pairs,
            '<p><b>Relie chaque longueur à son écriture en mètres.</b></p>',
            { ...opts(L1), ...(leurres.length ? { cibles_leurres: leurres } : {}) },
        );
        payload._meta.params = { leurres };
        return payload;
    }
    return makeRelier(
        [{ source: '19 dam', target: '190 m' }, { source: '19 cm', target: '0,19 m' },
            { source: '19 mm', target: '0,019 m' }],
        '<p><b>Relie chaque longueur à son écriture en mètres.</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : classer (mêmes longueurs, autres unités) ──────────────────

const genClasser = () => {
    // Version piégeuse (décision prof 2026-08-21) : MÊMES chiffres partout,
    // catégories « = M m » / « ≠ M m ». Les vraies écritures (0,007 km,
    // 700 cm...) côtoient les fausses aux mêmes chiffres mais au mauvais
    // rang (0,007 cm, 70 cm...) : seul le raisonnement d'unité classe.
    for (let essai = 0; essai < 80; essai++) {
        const M = randInt(2, 99);
        const cible = M * 10 ** 6;                    // M mètres, en µm
        const bons = [];
        const faux = [];
        const vus = new Set();
        for (const u of shuffle([...UNITES])) {
            // la vraie écriture de M mètres dans u
            const vrai = microVersFr(cible, u.sym);
            if (vrai !== null && vrai.replace(/[\s,]/g, '').length <= 5 && !vus.has(`${vrai} ${u.sym}`)) {
                if (bons.length < 3) {
                    bons.push(`${vrai} ${u.sym}`);
                    vus.add(`${vrai} ${u.sym}`);
                }
            }
            // une écriture aux MÊMES chiffres, décalée d'un ou deux rangs
            const j = randomChoice([-2, -1, 1, 2]);
            const facteur = 10 ** Math.abs(j);
            const microFaux = j > 0 ? cible * facteur : (cible % facteur === 0 ? cible / facteur : null);
            if (microFaux === null || faux.length >= 3) continue;
            const fauxFr = microVersFr(microFaux, u.sym);
            if (fauxFr === null || fauxFr.replace(/[\s,]/g, '').length > 5) continue;
            if (vus.has(`${fauxFr} ${u.sym}`)) continue;
            faux.push(`${fauxFr} ${u.sym}`);
            vus.add(`${fauxFr} ${u.sym}`);
        }
        if (bons.length < 2 || faux.length < 2) continue;
        const categories = [
            { name: `= ${M} m`, items: bons.slice(0, randInt(2, 3)) },
            { name: `≠ ${M} m`, items: faux.slice(0, randInt(2, 3)) },
        ];
        const tous = categories.flatMap((cat) => cat.items);
        if (new Set(tous).size !== tous.length) continue;
        // garde : chaque item re-vérifié dans sa colonne
        for (const item of categories[0].items) {
            const m = item.match(/^([\d\s  ,]+) (\w+)$/);
            garde(frVersMicro(m[1].trim(), m[2]) === cible, 'classer : « = » faux');
        }
        for (const item of categories[1].items) {
            const m = item.match(/^([\d\s  ,]+) (\w+)$/);
            garde(frVersMicro(m[1].trim(), m[2]) !== cible, 'classer : « ≠ » vrai');
        }
        return makeClasser(
            categories,
            '<p><b>Range chaque longueur dans la bonne colonne.</b></p>',
            opts(L1),
        );
    }
    return makeClasser(
        [
            { name: '= 7 m', items: ['0,007 km', '700 cm'] },
            { name: '≠ 7 m', items: ['0,007 cm', '70 cm'] },
        ],
        '<p><b>Range chaque longueur dans la bonne colonne.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'le mètre est TOUJOURS l\'une des deux unités (garde qui throw) ; l\'autre parcourt km..mm',
        '≤ 3 décimales des deux côtés (CM2 = millièmes), ≤ 4 chiffres avant la virgule ; donnée ≠ 1 (anti-commutativité du format « car »)',
        'arithmétique en micro-mètres ENTIERS, jamais de flottant ; jamais de tableau de conversion',
    ],
    convm_qcm_unique: {
        support: 'une conversion « donnée unité = ... unité », les deux sens',
        fixes: {
            sens_inverse: 'la virgule a glissé du mauvais côté (250 cm → 25 000 m)',
            rang_de_trop: 'un rang de glissement en trop',
            rang_de_moins: 'un rang de glissement en moins',
        },
    },
    convm_relier: {
        support: 'MÊME nombre dans toutes les sources (19 dam, 19 cm, 19 mm...) : les cibles en mètres ne diffèrent que par la virgule, l\'appariement par ressemblance est impossible',
        fixes: { rang_de_trop: 'le glissement fautif d\'une source, vérifié différent de toute valeur juste' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    convm_texte_trou_saisie: genConversionTrouSaisie,
    convm_qcm_unique: genQcm,
    convm_tableau_vf: genVraiFaux,
    convm_relier: genRelier,
    convm_classer: genClasser,
};

const FORMAT_LABELS = {
    convm_texte_trou_saisie: 'Saisie',
    convm_qcm_unique: 'QCM',
    convm_tableau_vf: 'Vrai/Faux',
    convm_relier: 'Relier',
    convm_classer: 'Classer',
};

export const CATEGORIES = [
    {
        id: 'convm',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
